// Shared types and display helpers for vehicle servicing, incidents and
// off-road tracking. No figure is computed here: every amount arrives already
// summed by Postgres, per CLAUDE.md rule 4. What lives here is labelling,
// tone and formatting.
import type { StatusTone } from './fleet'

export type MaintenanceCategory =
  | 'service'
  | 'tyres'
  | 'brakes'
  | 'roadworthy'
  | 'other'
  | 'fleetly_cost'
  | 'panel_beating'
  | 'mechanical'

// Order is the order of the columns on the servicing page. Panel beating and
// mechanical (3 October 2026, migration 3100) sit beside the workbook's four
// because they were carved out of 'other'.
export const MAINTENANCE_CATEGORIES: { code: MaintenanceCategory; label: string }[] = [
  { code: 'service', label: 'Service' },
  { code: 'tyres', label: 'Tyres' },
  { code: 'brakes', label: 'Brakes' },
  { code: 'roadworthy', label: 'Roadworthy' },
  { code: 'panel_beating', label: 'Panel beating' },
  { code: 'mechanical', label: 'Mechanical repairs' },
  { code: 'other', label: 'Other' },
  { code: 'fleetly_cost', label: 'Fleetly cost' },
]

/** Who carries a line: decided by the charge rule, or overridden on the line. */
export type BorneBy = 'owner' | 'driver' | 'fleetly'

export const BORNE_LABEL: Record<BorneBy, string> = {
  owner: 'Owner',
  driver: 'Driver',
  fleetly: 'Fleetly',
}

export const BORNE_TONE: Record<BorneBy, StatusTone> = {
  owner: 'amber',
  driver: 'red',
  fleetly: 'brand',
}

/** Null is "no rule covers this month", which is shown as unknown, never as Fleetly. */
export function borneLabel(b: string | null | undefined): string {
  if (!b) return 'No rule'
  return BORNE_LABEL[b as BorneBy] ?? b
}

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  MAINTENANCE_CATEGORIES.map((c) => [c.code, c.label]),
)

export function categoryLabel(code: string | null | undefined): string {
  if (!code) return 'Unknown'
  return CATEGORY_LABELS[code] ?? code
}

export type MaintenanceMonthRow = {
  vehicle_id: string
  period_start: string
  registration_number: string | null
  make: string | null
  model: string | null
  owner_id: string | null
  owner_name: string | null
  service_ex_vat_zar: number
  tyres_ex_vat_zar: number
  brakes_ex_vat_zar: number
  roadworthy_ex_vat_zar: number
  other_ex_vat_zar: number
  fleetly_ex_vat_zar: number
  panel_beating_ex_vat_zar: number
  mechanical_ex_vat_zar: number
  total_ex_vat_zar: number
  total_vat_zar: number
  total_incl_vat_zar: number
  // Null means either no charge rule covers the period, or the signed-in
  // account has no finance access. Both are "withheld", never zero. The three
  // payer figures sum to total_incl_vat_zar when all three are known.
  owner_charge_zar: number | null
  driver_charge_zar: number | null
  fleetly_borne_zar: number | null
  owner_carries_vat: boolean | null
  driver_carries_vat: boolean | null
  driver_unnamed_count: number
  override_count: number
  invoice_count: number
  lines_without_invoice: number
  statement_issued: boolean
  line_count: number
  last_changed_at: string | null
}

export type LineChange = {
  kind: 'category' | 'borne_by' | 'date'
  from: string | null
  to: string | null
  from_borne_by: string | null
  to_borne_by: string | null
  reason: string
  statement_issued: boolean
  actor_email: string | null
  at: string
}

/** One line of maintenance_breakdown_v1. */
export type BreakdownLine = {
  id: string
  vehicle_id: string
  registration_number: string | null
  period_start: string
  category: MaintenanceCategory
  amount_ex_vat_zar: number
  vat_zar: number
  total_incl_vat_zar: number
  description: string | null
  incurred_on: string | null
  odometer_km: number | null
  source: string
  source_ref: string | null
  created_at: string
  created_by_name: string | null
  invoice_id: string | null
  borne_by: BorneBy | null
  borne_by_override: BorneBy | null
  borne_reason: string | null
  driver_id: string | null
  driver_name: string | null
  driver_repair_case_id: string | null
  driver_repair_case_ref: string | null
  driver_repair_case_status: string | null
  statement_issued: boolean
  changes: LineChange[]
}

export type InvoiceItem = {
  id: string
  position: number
  description: string
  quantity: number | null
  amount_ex_vat_zar: number | null
  vat_zar: number | null
  amount_incl_vat_zar: number | null
  suggested_category: MaintenanceCategory | null
  suggested_reason: string | null
  confidence: 'high' | 'medium' | 'low' | null
  proposed_category: MaintenanceCategory | null
  matched_line_id: string | null
  line_id: string | null
  resolution: 'kept' | 'moved' | 'split' | 'recorded' | null
  resolved_at: string | null
}

export type BreakdownInvoice = {
  id: string
  vehicle_id: string
  registration_number: string | null
  period_start: string
  supplier_name: string | null
  invoice_number: string | null
  invoice_date: string | null
  amount_excl_vat_zar: number | null
  vat_zar: number | null
  total_zar: number | null
  filename: string | null
  mime_type: string | null
  has_file: boolean
  proposed_category: MaintenanceCategory | null
  read_status: 'pending' | 'read' | 'failed'
  read_error: string | null
  read_at: string | null
  warnings: string[]
  source: 'upload' | 'cash_repair'
  uploaded_by_email: string | null
  received_at: string | null
  line_ids: string[]
  items: InvoiceItem[]
}

export type Breakdown = {
  can_view_finance: boolean
  lines: BreakdownLine[]
  invoices: BreakdownInvoice[]
}

/**
 * Whether the reader disagrees with the person on an item. Only an item with
 * both a suggestion and a proposal can disagree; an item with no proposal is
 * a suggestion and nothing more.
 */
export function readerDisagrees(item: Pick<InvoiceItem, 'suggested_category' | 'proposed_category'>): boolean {
  return Boolean(item.suggested_category && item.proposed_category && item.suggested_category !== item.proposed_category)
}

export type MaintenanceLineRow = {
  id: string
  vehicle_id: string
  registration_number: string | null
  period_start: string
  category: MaintenanceCategory
  amount_ex_vat_zar: number
  vat_zar: number
  total_incl_vat_zar: number
  description: string | null
  incurred_on: string | null
  odometer_km: number | null
  ticket_id: string | null
  invoice_id: string | null
  incident_id: string | null
  source: string
  source_ref: string | null
  created_at: string
  created_by_name: string | null
}

export type OffRoadRow = {
  off_road_id: string
  vehicle_id: string
  registration_number: string | null
  make: string | null
  model: string | null
  lifecycle_status: string
  in_general_fleet: boolean | null
  current_location: string | null
  owner_id: string | null
  owner_name: string | null
  owner_phone: string | null
  manager_name: string | null
  started_at: string
  days_off_road: number
  expected_return_at: string | null
  next_update_date: string | null
  reason: string | null
  reason_category: string | null
  last_update_note: string | null
  last_update_at: string | null
  ticket_id: string | null
  incident_id: string | null
  incident_type: string | null
  incident_status: string | null
  update_state: 'no_update_date' | 'update_overdue' | 'update_due_today' | 'on_track'
  update_days_overdue: number | null
  return_overdue: boolean
  incident_already_resolved: boolean
}

export type IncidentRow = {
  incident_id: string
  vehicle_id: string | null
  registration_number: string | null
  make: string | null
  model: string | null
  lifecycle_status: string | null
  owner_id: string | null
  owner_name: string | null
  owner_email: string | null
  owner_phone: string | null
  driver_id: string | null
  driver_name: string | null
  driver_unmatched: boolean
  incident_type: string
  board_incident_type: string | null
  sub_type: string | null
  severity: string | null
  status: string
  title: string | null
  description: string | null
  occurred_at: string | null
  reported_at: string | null
  reported_by_name: string | null
  reported_by_email: string | null
  location_text: string | null
  current_location: string | null
  drivable: boolean | null
  operational: boolean | null
  police_attended: boolean | null
  case_number: string | null
  urgency: string | null
  immediate_action: string | null
  informed: string | null
  outcome: string | null
  move_to: string | null
  assigned_to: string | null
  next_update_date: string | null
  cost_zar: number | null
  estimated_cost_zar: number | null
  daily_storage_cost_zar: number | null
  odometer_km: number | null
  evidence_urls: string[] | null
  evidence_count: number
  at_fault: boolean | null
  fault_basis: string | null
  fault_determined_by_name: string | null
  fault_determined_at: string | null
  maintenance_ticket_id: string | null
  intake_source: string | null
  resolution_notes: string | null
  days_since: number | null
  update_state: 'closed' | 'no_update_date' | 'update_overdue' | 'update_due_today' | 'on_track'
  vehicle_off_road: boolean
  off_road_id: string | null
  // Added 24 August 2026 with the incident update thread and the owner report.
  incident_time: string | null
  injuries: boolean | null
  other_vehicles_involved: boolean | null
  context_next_steps: string | null
  first_knowledge_date: string | null
  // Whether the owner is reachable at all is a property of the commercial model,
  // not of the email address: an asset management vehicle can carry an owner
  // record the report router will never write to.
  commercial_model: string | null
  update_count: number
  last_update_at: string | null
  last_update_note: string | null
  report_sent: boolean
  report_recipient: string | null
  report_status: string | null
  // Added 1 October 2026, migration 1600: the person who owes the next update,
  // and what missed updates have raised against them.
  assigned_person_id: string | null
  in_charge_name: string | null
  assigned_since: string | null
  offerings_raised: number
  offerings_owed_zar: number | string | null
  next_offering_on: string | null
  // Added 27 October 2026, migration 9100: set when the owner signed the
  // Manage My Car transfer, after which incidents on this car are internal.
  incidents_internal_since: string | null
  incidents_internal_reason: string | null
}

export const INCIDENT_TYPES: { code: string; label: string }[] = [
  { code: 'accident', label: 'Accident' },
  { code: 'mechanical', label: 'Mechanical' },
  { code: 'service_maintenance', label: 'Service / Maintenance' },
  { code: 'licensing_compliance', label: 'Licensing / Compliance' },
  { code: 'safety_behaviour', label: 'Safety / Behaviour' },
  { code: 'other', label: 'Other' },
]

export const INCIDENT_STATUSES: { code: string; label: string }[] = [
  { code: 'open', label: 'Open' },
  { code: 'investigating', label: 'Investigating' },
  { code: 'escalated', label: 'Escalated' },
  { code: 'resolved', label: 'Resolved' },
  { code: 'closed', label: 'Closed' },
]

export const OFF_ROAD_REASONS: { code: string; label: string }[] = [
  { code: 'accident', label: 'Accident' },
  { code: 'mechanical', label: 'Mechanical' },
  { code: 'service', label: 'Service' },
  { code: 'roadworthy', label: 'Roadworthy' },
  { code: 'licensing_compliance', label: 'Licensing / compliance' },
  { code: 'insurance_assessment', label: 'With the insurer' },
  { code: 'awaiting_parts', label: 'Awaiting parts' },
  { code: 'awaiting_quote', label: 'Awaiting quote' },
  { code: 'impound', label: 'Impounded' },
  { code: 'written_off', label: 'Written off' },
  { code: 'unknown', label: 'Not recorded' },
]

const LABEL_MAPS: Record<string, Record<string, string>> = {
  incidentType: Object.fromEntries(INCIDENT_TYPES.map((t) => [t.code, t.label])),
  incidentStatus: Object.fromEntries(INCIDENT_STATUSES.map((t) => [t.code, t.label])),
  offRoadReason: Object.fromEntries(OFF_ROAD_REASONS.map((t) => [t.code, t.label])),
}

function look(map: string, code: string | null | undefined, fallback = 'Unknown'): string {
  if (!code) return fallback
  return LABEL_MAPS[map]?.[code] ?? code.replace(/_/g, ' ')
}

export const incidentTypeLabel = (c: string | null | undefined) => look('incidentType', c)
export const incidentStatusLabel = (c: string | null | undefined) => look('incidentStatus', c)
export const offRoadReasonLabel = (c: string | null | undefined) =>
  look('offRoadReason', c, 'Not recorded')

export function incidentTypeTone(code: string | null | undefined): StatusTone {
  switch (code) {
    case 'accident': return 'red'
    case 'mechanical': return 'amber'
    case 'licensing_compliance': return 'brand'
    case 'safety_behaviour': return 'red'
    case 'service_maintenance': return 'green'
    default: return 'neutral'
  }
}

export function incidentStatusTone(code: string | null | undefined): StatusTone {
  switch (code) {
    case 'open': return 'amber'
    case 'investigating': return 'brand'
    case 'escalated': return 'red'
    case 'resolved': return 'green'
    case 'closed': return 'neutral'
    default: return 'neutral'
  }
}

// Urgency, not severity, is the triage signal: the incident form asks urgency on
// every branch and answers it 209 times out of 210, while severity is asked only
// on the accident and mechanical branches and is missing from 185 of them.
export function urgencyTone(urgency: string | null | undefined): StatusTone {
  switch ((urgency ?? '').toLowerCase()) {
    case 'high': return 'red'
    case 'medium': return 'amber'
    case 'low': return 'neutral'
    default: return 'neutral'
  }
}

export type UpdateState = OffRoadRow['update_state'] | 'closed'

export function updateStateChip(state: UpdateState, daysOverdue: number | null): {
  tone: StatusTone
  label: string
} {
  switch (state) {
    case 'update_overdue':
      return {
        tone: 'red',
        label: daysOverdue && daysOverdue > 0 ? `Update ${daysOverdue}d overdue` : 'Update overdue',
      }
    case 'update_due_today':
      return { tone: 'amber', label: 'Update due today' }
    case 'no_update_date':
      // Deliberately red, not neutral. A vehicle nobody has committed to chase
      // is a worse state than one with a date that has slipped, and colouring it
      // grey is how five vehicles sat for over a hundred days each.
      return { tone: 'red', label: 'No update date' }
    case 'closed':
      return { tone: 'neutral', label: 'Closed' }
    default:
      return { tone: 'green', label: 'On track' }
  }
}

// Rands, no decimals, thin space grouping. Null renders as an em dash, which is
// the existing data marker in this codebase and predates the no-dash writing
// rule, per CLAUDE.md section 9.
export function zar(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `R${n.toLocaleString('en-ZA', { maximumFractionDigits: 0, minimumFractionDigits: 0 })}`
}

export function zarExact(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `R${n.toLocaleString('en-ZA', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`
}

/**
 * A month as 'YYYY-MM-01', whatever shape it arrived in.
 *
 * A Postgres date crosses the Worker as a JavaScript Date (postgres.js parses
 * it), so in production period_start reaches the browser as
 * '2026-09-01T00:00:00.000Z', while a value built here or read from jsonb is
 * '2026-09-01'. Comparing or sending them raw is how the first drill-down on
 * 3 October 2026 answered "2026-09-01T00:00:00.000Z is not a month". The
 * Worker runs in UTC, so the date part of the timestamp is the date itself.
 */
export function monthStart(value: string | null | undefined): string | null {
  if (!value) return null
  const m = String(value).match(/^(\d{4})-(\d{2})-\d{2}/)
  return m ? `${m[1]}-${m[2]}-01` : null
}

export function monthLabel(period: string | null | undefined): string {
  if (!period) return '—'
  const d = new Date(period)
  if (Number.isNaN(d.getTime())) return period
  return d.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })
}

/** First day of the month, N months back from today, as 'YYYY-MM-01'. */
export function monthsAgo(n: number): string {
  const d = new Date()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() - n)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`
}

// ---------------------------------------------------------------------------
// The incident report form.
//
// These lists mirror the Vehicle Incident Report Typeform (QFOc6Qjl) question
// for question, so a report filed at a desk and one filed on a phone produce the
// same record. The labels are sent verbatim as incident_type and sub_type:
// record_incident_intake_v1 maps the label to the incident_type enum and keeps
// the label itself in board_incident_type, which is what the 210 historic
// submissions already carry. Sending the enum code instead would split the board
// into two vocabularies for the same thing.
// ---------------------------------------------------------------------------

/** Typeform's own wording, in its own order. */
export const INCIDENT_FORM_TYPES = [
  'Accident',
  'Mechanical Issue',
  'Service / Maintenance',
  'Safety / Behaviour',
  'Licensing / Compliance',
  'Other',
] as const
export type IncidentFormType = (typeof INCIDENT_FORM_TYPES)[number]

export const SEVERITY_OPTIONS = ['Minor', 'Moderate', 'Major', 'Critical'] as const
export const URGENCY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'] as const
export const DRIVABLE_OPTIONS = ['Yes', 'No', 'Limited'] as const
export const INFORMED_OPTIONS = [
  'Ops', 'Maintenance', 'Insurance', 'Owner', 'Driver', 'Management',
] as const

/** Branch-specific question. Keyed by the incident type that asks it. */
export const SUB_TYPE_OPTIONS: Record<string, string[]> = {
  'Mechanical Issue': [
    'Engine', 'Gearbox / transmission', 'Brakes', 'Suspension', 'Electrical',
    'Tyres', 'Cooling', 'Bodywork', 'Other',
  ],
  'Service / Maintenance': [
    'Scheduled service', 'Unscheduled repair', 'Tyres', 'Brakes', 'Battery',
    'Panel and paint', 'Other',
  ],
  'Safety / Behaviour': [
    'Reckless driving', 'Unauthorised use', 'Smoking in vehicle',
    'Passenger complaint', 'Vehicle cleanliness', 'Other',
  ],
  'Licensing / Compliance': [
    'Licence disc expired', 'Impounded', 'Traffic fine', 'Roadworthy',
    'Operating licence', 'Other',
  ],
}

/** A row of v_incident_form_vehicles. */
export type IncidentFormVehicle = {
  vehicle_id: string
  registration_number: string | null
  descriptor: string | null
  label: string | null
  lifecycle_status: string | null
  commercial_model: string | null
  current_location: string | null
  operational: boolean | null
  owner_id: string | null
  owner_name: string | null
  owner_email: string | null
  // Mirrors incident_report_route_v1. Not derived from owner_email alone: an
  // asset management vehicle can have an owner record with an address the
  // router will never write to.
  owner_will_be_notified: boolean
  // Migration 9100: why the owner is not told, when they signed the transfer.
  incidents_internal_reason: string | null
}

/** A row of v_incident_form_drivers. */
export type IncidentFormDriver = {
  driver_id: string
  person_id: string
  driver_name: string | null
  primary_phone: string | null
  driver_status: string | null
  current_vehicle_id: string | null
  current_registration: string | null
  current_seat: string | null
}

/** A row of the getIncidentUpdates feed. */
export type IncidentUpdateRow = {
  id: string
  incident_id: string
  note: string
  status_before: string | null
  status_after: string | null
  current_location: string | null
  move_to: string | null
  next_update_date: string | null
  cost_zar: number | null
  operational: boolean | null
  outcome: string | null
  assigned_to: string | null
  owner_notified: boolean
  created_at: string
  actor_name: string | null
}

/**
 * Yes, No, or unanswered.
 *
 * Unanswered is a real answer and must never be sent as No. "Injuries: FALSE"
 * on a report nobody answered is a statement Fleetly cannot stand behind, and it
 * reaches a vehicle owner.
 */
export type TriStateValue = 'yes' | 'no' | 'unset'

export function triStatePayload(v: TriStateValue): 'yes' | 'no' | undefined {
  if (v === 'yes') return 'yes'
  if (v === 'no') return 'no'
  return undefined
}
